<template>
  <v-app>
    <div class="app-container">
      <!-- Header -->
      <header-component
        v-if="!isLandingPage"
        :title="title"
        class="app-header"
      />
      <!-- Sidebar -->
      <side-bar-component
        v-if="!isLandingPage"
        class="app-sidebar"
        @item-clicked="handleItemClick"
      />

      <!-- Main Layout with Explorer and Content -->
      <div class="main-layout">
        <!-- Inventory Explorer - Fixed Position with integrated resizer -->
        <div
          v-if="!isLandingPage && showInventoryExplorerPage"
          class="explorer-container"
          :style="{
            width: explorerCollapsed
              ? explorerCollapsedWidth + 'px'
              : sidebarWidth + 12 + 'px',
            left: (sidebarExpanded ? 180 : 60) + 'px',
            transition: explorerCollapsed
              ? 'width 0.3s ease, left 0.3s ease'
              : 'width 0.2s ease, left 0.3s ease'
          }"
        >
          <div
            class="explorer"
            :style="{
              width: explorerCollapsed
                ? explorerCollapsedWidth + 'px'
                : sidebarWidth + 'px'
            }"
          >
            <inventory-explorer
              v-show="showInventoryExplorer"
              :categories="categories"
              :folders="folders"
              @categories_with_items="handleCategoriesWithItems"
              @folder-clicked="handleFolderClicked"
              @explorer-toggled="handleExplorerToggled"
            />
          </div>

          <!-- Integrated Resizer with collapse/expand control -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <div
                class="resizer-integrated"
                :class="{ 'resizing-active': isResizing }"
                v-bind="attrs"
                v-on="on"
                @mousedown="startResize"
              >
                <!-- Collapse/Expand toggle placed inside the resizer strip -->
                <div
                  class="collapse-toggle"
                  @click.stop="toggleExplorerCollapse"
                >
                  <v-icon class="collapse-icon">
                    {{
                      explorerCollapsed
                        ? 'mdi-chevron-right'
                        : 'mdi-chevron-left'
                    }}
                  </v-icon>
                </div>
                <svg
                  width="12"
                  height="48"
                  viewBox="0 0 12 48"
                  xmlns="http://www.w3.org/2000/svg"
                  class="grip-icon"
                >
                  <circle cx="6" cy="6" r="2" fill="#888" />
                  <circle cx="6" cy="16" r="2" fill="#888" />
                  <circle cx="6" cy="26" r="2" fill="#888" />
                  <circle cx="6" cy="36" r="2" fill="#888" />
                </svg>
              </div>
            </template>
            <span>{{
              explorerCollapsed
                ? 'Expand explorer'
                : 'Drag to resize or click to collapse'
            }}</span>
          </v-tooltip>
        </div>

        <!-- Main content with proper margins -->
        <v-main class="content" :style="contentStyle">
          <router-view />
        </v-main>
      </div>

      <!-- Chatbot Assistant (always visible except on landing page) -->
      <chatbot-assistant v-if="!isLandingPage" />
    </div>
  </v-app>
</template>

<script>
import SideBarComponent from '@/components/SidebarComponent'
import Header from '@/components/Layout/Header'
import InventoryExplorer from '@/components/InventoryExplorer.vue'
import ChatbotAssistant from '@/components/ChatbotAssistant.vue'
import api from '@/api/api.js'
import { mapState, mapActions } from 'vuex'
import { EventBus } from '@/eventBus.js'

export default {
  name: 'App',
  components: {
    SideBarComponent,
    'header-component': Header,
    'inventory-explorer': InventoryExplorer,
    'chatbot-assistant': ChatbotAssistant
  },
  data() {
    return {
      fooYear: 'INVENTORY 2025',
      previousSidebarWidth: 240,
      items_clicked: false,
      showInventoryExplorer: false,
      // Load explorer data (folders/categories) only once lazily
      explorerDataLoaded: false,
      explorerLoading: false,
      sidebarExpanded: false,
      sidebarWidth: 240,
      isResizing: false,
      explorerCollapsed: false,
      explorerMaxWidth: 400,
      explorerCollapsedWidth: 60,
      items: [], // kept for compatibility; no longer fetched here
      loading: false,
      categories: [],
      folders: [],
      no_category: {
        items: [],
        organization_id: 1,
        id: 'no_category',
        name: 'No Category',
        parent_category_id: '0',
        created_by: '',
        modified_at: '',
        description: '',
        created_at: '',
        modified_by: ''
      },
      error: ''
    }
  },
  computed: {
    ...mapState({
      title: (state) => state.route.meta.title
    }),

    isLandingPage() {
      return this.$route.path === '/landing'
    },
    showInventoryExplorerPage() {
      const path = this.$route.path
      // Allow optional /categories, /categories/<id>, /categories/no_category
      const categoriesPattern = /^\/categories(?:\/(?:\d+|no_category))?$/
      // Only root items page (exclude item detail pages) just like previous logic
      const itemsPattern = /^\/items$/
      // Allow /folders and /folders/<id>
      const foldersPattern = /^\/folders(?:\/\d+)?$/
      return (
        categoriesPattern.test(path) ||
        itemsPattern.test(path) ||
        foldersPattern.test(path)
      )
    },
    // Only offset main content by explorer width; drawer offset handled by Vuetify app layout
    contentStyle() {
      const explorerVisible =
        !this.isLandingPage && this.showInventoryExplorerPage
      const explorerWidth = explorerVisible
        ? this.explorerCollapsed
          ? this.explorerCollapsedWidth
          : this.sidebarWidth + 12
        : 0
      return {
        marginLeft: explorerWidth + 'px',
        transition: 'margin-left 0.3s ease'
      }
    }
  },
  watch: {
    '$route.path'(newPath) {
      this.updateInventoryExplorerVisibility(newPath)
    }
  },
  async created() {
    // Enhanced auth check with session recovery
    try {
      await this.authCheck()

      // Additional check for Auth0 state after app mount
      if (this.$auth0) {
        await new Promise((resolve) => setTimeout(resolve, 100)) // Wait for Auth0 initialization

        // If Auth0 says not authenticated but we have stored user info, try recovery
        if (!this.$auth0.isAuthenticated) {
          const hasStoredUserInfo = localStorage.getItem('user_info')
          const useSSO = localStorage.getItem('USE_SSO')

          if (hasStoredUserInfo && useSSO === 'true') {
            try {
              // Try to get token silently which may restore the session
              const token = await this.$auth0.getTokenSilently({
                ignoreCache: false,
                timeoutInSeconds: 5
              })
              if (token) {
                await this.$auth0.refreshState()
              }
            } catch (recoveryError) {
              // Try the storage recovery method
              if (this.$auth0.tryRecoverFromStorage) {
                this.$auth0.tryRecoverFromStorage()
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn('Auth check failed:', error)
    }

    // Determine initial explorer visibility and lazy-load if needed
    this.updateInventoryExplorerVisibility(this.$route.path)

    EventBus.$on('category-added', this.get_all_categories)
    EventBus.$on('folder-added', this.get_all_folders)
    EventBus.$on('category-deleted', this.get_all_categories)
  },
  beforeDestroy() {
    EventBus.$off('category-added', this.get_all_categories)
    EventBus.$off('folder-added', this.get_all_folders)
    EventBus.$off('category-deleted', this.get_all_categories)
  },
  methods: {
    handleItemClick(link) {
      if (link === 'sidebar-expand') return (this.sidebarExpanded = true)
      if (link === 'sidebar-collapse') return (this.sidebarExpanded = false)

      this.items_clicked = link.includes('/items')
      this.showInventoryExplorer = this.items_clicked

      // Do not trigger bulk loads here; explorer data loads lazily on visibility

      if (this.$route.path !== link) {
        this.$router.push(link).catch((err) => {
          if (err.name !== 'NavigationDuplicated') throw err
        })
      }
    },
    handleFolderClicked(folder) {
      const paths = {
        main: '/items',
        folder: `/folders/${folder.id}`,
        category: `/categories/${folder.id}`,
        'main-folder': '/folders',
        'main-category': '/categories'
      }
      const path = paths[folder.type]
      if (path && this.$route.path !== path) {
        this.$router.push(path).catch((err) => {
          if (err.name !== 'NavigationDuplicated') throw err
        })
      }
    },
    // Lightweight, on-demand loads for explorer
    async get_all_categories() {
      try {
        const res = await api.get('/categories')
        this.categories = Array.isArray(res.data) ? res.data : []
        // Ensure "No Category" sentinel exists
        if (!this.categories.find((c) => c.id === 'no_category')) {
          this.categories.push(this.no_category)
        }
      } catch (err) {
        this.error = 'Failed to fetch categories: ' + err.message
      }
    },
    async get_all_folders() {
      // Prefer hierarchy endpoint to avoid large folder_items payloads, then flatten for explorer
      const flatten = (nodes, parentId = 0, acc = []) => {
        if (!Array.isArray(nodes)) return acc
        for (const node of nodes) {
          acc.push({ id: node.id, name: node.name, parent_folder_id: parentId })
          if (node.children && node.children.length)
            flatten(node.children, node.id, acc)
        }
        return acc
      }
      try {
        const res = await api.get('/folders/folders_hierarchy')
        this.folders = flatten(res.data, 0, [])
      } catch (err) {
        // Fallback to full list if hierarchy endpoint fails
        try {
          const res = await api.get('/folders')
          this.folders = Array.isArray(res.data) ? res.data : []
        } catch (e2) {
          this.error =
            'Failed to fetch folders: ' + (err?.message || e2?.message)
        }
      }
    },
    async ensureExplorerData() {
      if (this.explorerDataLoaded || this.explorerLoading) return
      this.explorerLoading = true
      try {
        await Promise.all([this.get_all_categories(), this.get_all_folders()])
        this.explorerDataLoaded = true
      } finally {
        this.explorerLoading = false
      }
    },
    handleCategoriesWithItems(data) {
      this.categories_with_items = data
    },
    handleExplorerToggled(explorerState) {
      this.explorerCollapsed = explorerState.isCollapsed
      this.explorerMaxWidth = explorerState.maxWidth
      this.explorerCollapsedWidth = explorerState.collapsedWidth

      // If collapsed, don't allow resizing
      if (this.explorerCollapsed) {
        this.sidebarWidth = this.explorerCollapsedWidth
      } else if (this.sidebarWidth < 200) {
        // If expanding and width is too small, set to minimum
        this.sidebarWidth = 240
      }
    },
    updateInventoryExplorerVisibility(path) {
      const exclude = /\/items\/\d+/
      this.showInventoryExplorer =
        !exclude.test(path) &&
        ['/items', '/folders', '/categories', '/transactions'].some((p) =>
          path.includes(p)
        )
      // Lazy-load explorer data when it first becomes visible
      if (this.showInventoryExplorer) {
        this.ensureExplorerData()
      }
    },
    startResize(e) {
      if (this.explorerCollapsed) return // Don't allow resize when collapsed
      this.isResizing = true
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'
    },
    onResize(e) {
      if (this.isResizing && !this.explorerCollapsed) {
        const minWidth = 200
        const maxWidth = this.explorerMaxWidth
        const sidebarOffset = this.sidebarExpanded ? 180 : 60
        const newWidth = e.clientX - sidebarOffset
        this.sidebarWidth = Math.max(minWidth, Math.min(newWidth, maxWidth))
        console.log('Resizing to:', this.sidebarWidth)
      }
    },
    stopResize() {
      console.log('Stopping resize...')
      this.isResizing = false
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
      // Re-enable text selection and reset cursor
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    },
    toggleExplorerCollapse() {
      if (!this.explorerCollapsed) {
        // Collapse: remember current width and set to collapsed width
        this.previousSidebarWidth = this.sidebarWidth
        this.explorerCollapsed = true
        this.sidebarWidth = this.explorerCollapsedWidth
      } else {
        // Expand: restore previous width within bounds
        this.explorerCollapsed = false
        const minWidth = 200
        const maxWidth = this.explorerMaxWidth
        const restored = Math.max(
          minWidth,
          Math.min(this.previousSidebarWidth || 240, maxWidth)
        )
        this.sidebarWidth = restored
      }
    },
    ...mapActions('auth', { authCheck: 'authCheck' })
  }
}
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
.app-header {
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 5;
  height: 64px;
}
.main-layout {
  flex-grow: 1;
  overflow: visible;
  /* v-app-bar with app prop handles top offset */
  padding-top: 0;
  transition: margin-left 0.3s ease;
}

/* Explorer Container with integrated resizer */
.explorer-container {
  position: fixed;
  top: 72px;
  height: calc(100vh - 72px);
  z-index: 3;
  display: flex;
  align-items: stretch;
  background-color: #ffffff;
}

/* Explorer - Full Height and Independent */
.explorer {
  min-width: 60px;
  height: 100%;
  overflow: hidden;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.explorer ::v-deep .inventory-explorer {
  height: calc(100vh - 72px);
  max-height: calc(100vh - 72px);
  margin: 0;
  border-radius: 0;
  border: none;
  box-shadow: none;
  background: transparent;
  overflow-y: auto;
}

.explorer ::v-deep .explorer-content {
  height: calc(100vh - 72px - 48px);
  max-height: calc(100vh - 72px - 48px);
  overflow: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.explorer ::v-deep .explorer-tree {
  height: auto;
  max-height: none;
  flex: 1;
  overflow-y: auto;
}

/* Integrated Resizer */
.resizer-integrated {
  width: 12px;
  height: 100%;
  cursor: col-resize;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  user-select: none;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
  position: relative;
}

.resizer-integrated:hover {
  background-color: rgba(25, 118, 210, 0.1);
}

.resizer-integrated:active {
  background-color: rgba(25, 118, 210, 0.15);
}

.resizer-integrated:hover .grip-icon circle {
  fill: #1976d2;
}

.resizer-integrated:active .grip-icon circle {
  fill: #1565c0;
}

.grip-icon {
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.resizer-integrated:hover .grip-icon {
  opacity: 0.8;
}

.resizer-integrated:active .grip-icon {
  opacity: 1;
}

.resizer-integrated.resizing-active {
  background-color: rgba(25, 118, 210, 0.2);
}

.resizer-integrated.resizing-active .grip-icon circle {
  fill: #1565c0;
}

.resizer-integrated.resizing-active .grip-icon {
  opacity: 1;
}

/* Tiny collapse/expand toggle inside the resizer */
.collapse-toggle {
  position: absolute;
  top: 88px; /* slightly further down from the top */
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.collapse-icon {
  font-size: 23px !important; /* a little bit bigger */
  line-height: 1;
}

.content {
  position: relative;
  flex: 1;
  overflow-y: auto;
  /* remove extra top padding – Vuetify already offsets v-main by 72px */
  padding: 0 20px 20px; /* was 24px on all sides */
  background-color: #fafafa;
  min-height: calc(100vh - 72px);
  height: auto;
  margin-left: 0; /* dynamic margin applied inline */
}

/* Prevent the first element inside router-view from adding its own top gap */
:deep(.content > *:first-child) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Remove stacked padding when a v-container with utility classes is the first child */
:deep(.content > .v-container.pa-4:first-child) {
  padding-top: 0 !important;
}

/* Force Vuetify main wrapper top padding to exactly match app-bar height */
:deep(.v-main__wrap) {
  padding-top: 72px !important;
}

/* Optional tighter layout utility (apply class "tight-page" to v-container if needed) */
:deep(.content > .v-container.tight-page) {
  padding: 0 12px 16px !important;
}

.content.content-with-explorer {
  padding-left: 0;
}

.content-full {
  width: 100%;
  overflow-y: auto;
  padding: 24px;
  background-color: #fafafa;
  min-height: calc(100vh - 72px);
  height: auto;
}

/* (edge opener removed per current UX) */

/* Responsive design */
@media (max-width: 768px) {
  .main-layout {
    margin-left: 0 !important;
  }

  /* Sidebar responsive adjustments handled by Vuetify drawer */

  .main-flex {
    flex-direction: column;
  }

  .explorer {
    height: auto;
    min-height: 200px;
    max-height: 50vh;
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .resizer {
    display: none;
  }

  .content {
    flex: 1;
    min-height: 50vh;
    padding: 16px;
  }
}
</style>
